<template>
  <BaseAutocomplete
    v-model="modelValue"
    :options="options"
    :variant="props.variant"
    :option-label="props.optionLabel"
    :option-value="props.optionValue"
    :placeholder="props.placeholder"
    class="base-address-input"
    :disabled="props.disabled"
    :bg-color="props.bgColor"
    with-immediate-select
    with-rounded
    :offset-y="props.offsetY"
    @search="handleSearch"
    @select="handleSelect($event as OptionType | null)"
  >
    <template #append-icon>
      <slot name="append-icon">
        <BaseIcon
          v-if="props.appendIcon"
          :name="props.appendIcon"
          size="20px"
          :color="props.appendIconColor"
        />
      </slot>
    </template>
    <template #item="{ data }">
      <div class="base-address-input__item">
        <span>{{ data.value }}</span>
        <span v-show="getCaption(data.data)">{{ getCaption(data.data) }}</span>
      </div>
    </template>
    <template #empty-init>
      Введите адрес
    </template>
    <template #empty-data>
      <p class="base-address-input__empty-text">
        <span class="base-address-input__empty-text--title">
          Мы не нашли такой адрес в нашей базе данных
        </span>
        <br>
        Если вы убедились в правильности введённых данных, пожалуйста,
        продолжайте заполнение формы.
      </p>
    </template>
  </BaseAutocomplete>
</template>

<script setup lang="ts">
import type { DaDataApi } from 'dadata-api'

import BaseAutocomplete from '../autocomplete/index.vue'
import BaseIcon from '../icon/index.vue'

type OptionType = DaDataApi.Suggestion<DaDataApi.Address>
type SearchType = DaDataApi.AddressBounds

interface ResponseType {
  suggestions: Array<OptionType>
}

export interface Props {
  fromBound?: SearchType
  toBound?: SearchType
  locations?: Array<Record<string, string>>
  optionLabel?: string
  optionValue?: string
  disabled?: boolean
  placeholder?: string
  bgColor?: string
  offsetY?: number
  variant?: 'light'
  appendIcon?: string
  appendIconColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  bgColor: 'var(--color-surface-placeholder)',
  offsetY: 4,
  optionLabel: '{value}',
  optionValue: '{value}',
  appendIconColor: 'var(--color-foreground-primary)',
})

const emit = defineEmits<{
  (e: 'select', item: OptionType | null): void
}>()

const modelValue = defineModel<OptionType | string>({ required: true })
const options = ref<Array<OptionType>>([])

async function handleSearch(query: string) {
  const body = {
    query,
    ...(props.fromBound ? { from_bound: { value: props.fromBound } } : {}),
    ...(props.toBound ? { to_bound: { value: props.toBound } } : {}),
    ...(props.locations?.length ? { locations: props.locations } : {}),
  }
  const { data } = await useFetch<ResponseType>('/api/dadata', {
    method: 'POST',
    body: JSON.stringify(body),
  })

  const result = data.value?.suggestions ?? []

  options.value = result

  return result
}

function handleSelect(item: OptionType | null) {
  if (item === null) {
    options.value = []
  }
  emit('select', item)
}

function getCaption({ city, region_with_type }: DaDataApi.Address) {
  return [city, region_with_type].filter(Boolean).join(', ')
}
</script>

<style lang="scss" scoped>
.base-address-input {
  &__item {
    display: flex;
    flex-direction: column;
    gap: rem(4px);

    span:first-child {
      @include typo-text-secondary;

      white-space: normal;
    }

    span:last-child {
      @include typo-text-mini;

      color: var(--color-foreground-caption);
    }
  }

  &__empty-text {
    font-weight: 400;
    font-size: 14px;

    &--title {
      font-weight: 600;
    }
  }
}
</style>
